import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import WrapperOrders from "../../../components/shared/WrapperContent";
import OrdersContent from "../../../components/orders/content";
import style from "./OrdersAvailable.module.scss";
import DefaultPaginationNew from "../../../components/shared/DefaultPaginationNew";
import { Notification } from "../../../services";
import Loading from "../../../components/shared/Loading/Loading";
import EmptyContent from "../../../components/shared/emptyContent";
import { useSelector } from "react-redux";
import OrdersTable from "../../../components/orders-new/available/tender-orders/components/OrdersTable";
import HeaderContentNew from "../../../components/shared/HeaderContentNew/HeaderContentNew";
import { EXPEDITOR_TABS } from "../../../mock";
import { useOrderRealtimeEvents } from "../../../hooks/useOrderRealtimeEvents";
import { ORDER_LIST_REFRESH_TARGETS } from "../../../services/orderRealtimeEvents";
import Expeditor from "../../../services/Expeditor";
import OrdersTableMobile from "../../../components/orders-new/available/tender-orders/components/OrdersTableMobile";
import SwitchTabs from "../../../components/shared/SwitchTabs/SwitchTabs";
import OrderFilterExpeditor from "../../../components/orders-new/filter/OrderFilterExpeditor";
import { useLanguage } from "../../../context/LanguageContext";
import { ExpeditorOrdersTab } from "../../../components/orders-new/tabs";
import { useOrderFilter } from "../../../hooks/useOrderFilter";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { TRANSPORTATION_TYPES } from "../utils/railwayOrder";
import OrderInfoExpeditor from "../../../components/orders-new/order-info-expeditor/OrderInfoExpeditor";

const OrdersAvailablePage = ({ transportationType = TRANSPORTATION_TYPES.ROAD }) => {
  const isRailway = transportationType === TRANSPORTATION_TYPES.RAILWAY;
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const { user } = useSelector((state) => state);
  const { isTabletSmallView } = useScreenSize();

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [selectedLimit, setSelectedLimit] = useState(10);
  const [activeTab, setActiveTab] = useState("assigned");
  const isInitialPageFetch = useRef(true);

  const [countRefreshVersion, setCountRefreshVersion] = useState(0);
  const [activeTap, setActiveTap] = useState("all");
  const [activeStatuses, setActiveStatuses] = useState([]);
  const [activeStatus, setActiveStatus] = useState(() => {
    const defaultTab = EXPEDITOR_TABS(t).find((tab) => tab.key === "all");
    return defaultTab || EXPEDITOR_TABS(t)[0];
  });
  const [notificationsMap, setNotificationsMap] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeOrderId, setActiveOrderId] = useState(null);
  const ordersRequestIdRef = useRef(0);

  const {
    filterValues,
    filterSetters,
    filterMeta,
    filterDeps,
    isFilterOpen,
    setFilterOpen,
    resetFilter,
    getFilterParams,
  } = useOrderFilter(currentLanguage);

  const tabOptions = [
    { key: "assigned", text: t("ordersAvailablePage.assigned"), tooltip: t("tooltips.assignedOrdersList") },
    { key: "own", text: t("ordersAvailablePage.own"), tooltip: t("tooltips.createdOrdersList") },
  ];

  const getOrdersRequestParams = (page = currentPage, statuses = activeStatuses, key = activeTap) => ({
    ...getFilterParams({
      page,
      limit: selectedLimit,
      statusActive: { key, statuses },
      sorting: null,
      selectedIds,
      isExpeditor: true,
    }),
    transportationType,
  });

  const fetchData = async (
    statuses = activeStatuses,
    key = activeTap,
    page = currentPage,
    { silent = false } = {},
  ) => {
    const requestId = ++ordersRequestIdRef.current;
    if (!silent) {
      setLoading(true);
    }
    try {
      const params = getOrdersRequestParams(page, statuses, key);

      const response = await Expeditor.getOrderListForExpeditor(activeTab, params);
      const responseData = response.data;

      if (requestId !== ordersRequestIdRef.current) return;

      setOrders(responseData.rows || []);
      setOrdersTotal(responseData.total || 0);
    } catch (error) {
      if (requestId !== ordersRequestIdRef.current) return;

      console.error("Ошибка при получении заказов:", error);
      if (!silent) {
        setOrders([]);
        setOrdersTotal(0);
      }
    } finally {
      if (requestId === ordersRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const onDownloadExcel = async () => {
    try {
      const params = getOrdersRequestParams();

      const response = await Expeditor.getExpeditorExportOrders(activeTab, params);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      link.setAttribute("download", `orders_${activeTab}_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Ошибка при экспорте заказов:", error);
    }
  };

  const handleTabChange = (optionKey) => {
    setActiveTab(optionKey);
    setSelectedIds([]);
  };

  const handleRefetchData = () => {
    setOrders([]);
    fetchData();
    setCountRefreshVersion((version) => version + 1);
  };

  useEffect(() => {
    setSelectedIds([]);
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData(activeStatuses, activeTap);
    }
  }, [...filterDeps, selectedLimit, currentLanguage, transportationType]);

  useEffect(() => {
    if (isInitialPageFetch.current) {
      isInitialPageFetch.current = false;
      return;
    }

    fetchData(activeStatuses, activeTap);
  }, [currentPage, activeTab]);

  useEffect(() => {
    if (!orders || orders.length === 0) return;

    const fetchNotifications = async () => {
      const updatedMap = {};
      for (const item of orders) {
        try {
          const response = await Notification.getOrderNotificationById(item.id);
          updatedMap[item.id] = response?.data || [];
        } catch (err) {
          console.error("Ошибка при получении уведомлений для заказа:", item.id, err);
          updatedMap[item.id] = [];
        }
      }
      setNotificationsMap(updatedMap);
    };

    fetchNotifications();
  }, [orders]);

  const triggerForceUpdate = () => {
    fetchData(activeStatuses, activeTap, currentPage, { silent: true });
    setCountRefreshVersion((version) => version + 1);
  };

  useOrderRealtimeEvents(({ target }) => {
    if (!ORDER_LIST_REFRESH_TARGETS.has(target)) return;

    fetchData(activeStatuses, activeTap, currentPage, { silent: true });
    setCountRefreshVersion((version) => version + 1);
  });

  return (
    <>
      <HeaderContentNew
        title={t(isRailway ? "railwayOrder.listTitle" : "ordersAvailablePage.header.title")}
        displayReloadBtn
        onReload={handleRefetchData}
      />

      <OrderFilterExpeditor
        transportationType={transportationType}
        isOpen={isFilterOpen}
        onClose={() => setFilterOpen(false)}
        filterValues={filterValues}
        filterSetters={filterSetters}
        filterMeta={filterMeta}
        statusActive={activeStatus}
        onSwitchToAll={() => {
          const allTab = EXPEDITOR_TABS(t).find((tab) => tab.key === "all");
          if (allTab) {
            setActiveTap(allTab.key);
            setActiveStatuses(allTab.statuses);
            setActiveStatus(allTab);
          }
        }}
      />

      <WrapperOrders>
        <SwitchTabs
          options={tabOptions}
          activeOption={activeTab}
          onOptionChange={handleTabChange}
          className={style.tabsContainer}
        />

        <ExpeditorOrdersTab
          transportationType={transportationType}
          setStatus={(item) => {
            setActiveTap(item.key);
            setActiveStatuses(item.statuses);
            setActiveStatus(item);
            setSelectedIds([]);
            if (currentPage !== 1) {
              setCurrentPage(1);
            } else {
              fetchData(item.statuses, item.key, 1);
            }
          }}
          onDownloadExcel={onDownloadExcel}
          statusActive={activeStatus}
          activeTab={activeTab}
          isCallTaps={countRefreshVersion}
          isFilterIcon={isTabletSmallView}
          onOpenFilter={() => setFilterOpen((prev) => !prev)}
          onReset={resetFilter}
          // filter values for count API
          countryFrom={filterValues.countryFrom}
          countryTo={filterValues.countryTo}
          cityFrom={filterValues.cityFrom}
          cityTo={filterValues.cityTo}
          loadingDate={filterValues.loadingDate}
          creationDate={filterValues.creationDate}
          selectedStatus={filterValues.selectedStatus}
          selectedCurrency={filterValues.selectedCurrency}
          senderName={filterValues.senderName}
          receiverName={filterValues.receiverName}
          orderNo={filterValues.orderNo}
          executor={filterValues.executor}
          expeditorInsuranceIncluded={filterValues.expeditorInsuranceIncluded}
        />

        <OrdersContent>
          <div className={style.content}>
            {isLoading ? (
              <Loading inBlock />
            ) : orders.length === 0 ? (
              <EmptyContent />
            ) : (
              <>
                <OrdersTable
                  isRailway={isRailway}
                  onOpenOrder={isRailway ? setActiveOrderId : undefined}
                  items={orders || []}
                  isLoading={false}
                  companyName={user?.companyName}
                  notificationsMap={notificationsMap}
                  setNotificationsMap={setNotificationsMap}
                  onForceUpdate={triggerForceUpdate}
                  isShowOrderSolution
                  isAvailablePage
                  isOwn={activeTab === "own"}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                  isExpeditor
                />

                <OrdersTableMobile
                  isRailway={isRailway}
                  onOpenOrder={isRailway ? setActiveOrderId : undefined}
                  items={orders || []}
                  isLoading={false}
                  companyName={user?.companyName}
                  notificationsMap={notificationsMap}
                  setNotificationsMap={setNotificationsMap}
                  onForceUpdate={triggerForceUpdate}
                  isShowOrderSolution
                  isAvailablePage
                />
              </>
            )}
          </div>

          <DefaultPaginationNew
            total={ordersTotal}
            currentPage={currentPage}
            limit={selectedLimit}
            changePage={setCurrentPage}
            changeLimit={setSelectedLimit}
            isLoading={isLoading}
          />
        </OrdersContent>
      </WrapperOrders>
      {isRailway && (
        <OrderInfoExpeditor
          isOpen={activeOrderId !== null}
          setIsOpen={(isOpen) => {
            if (!isOpen) setActiveOrderId(null);
          }}
          orderId={activeOrderId}
          companyName={user?.companyName}
          onForceUpdate={triggerForceUpdate}
        />
      )}
    </>
  );
};

export default OrdersAvailablePage;
